import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import EnquiryDialog from "./EnquiryDialog";

export default function Header() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showEnq, setShowEnq] = useState(false);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/products", label: t("nav.products") },
    { to: "/refurbished", label: t("nav.refurbished") },
    { to: "/services", label: t("nav.services") },
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0F2942]">
            <FlaskConical className="h-5 w-5 text-teal-300" />
          </div>
          <div className="leading-tight">
            <div className="text-base font-bold tracking-tight text-[#0F2942]" style={{ fontFamily: "Outfit, sans-serif" }}>HiCare</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-teal-600">Lab Solutions</div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-[#0F2942]" : "text-slate-600 hover:text-[#0F2942]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher />
          <Button
            data-testid="header-enquiry-btn"
            onClick={() => setShowEnq(true)}
            className="bg-[#0F2942] text-white hover:bg-[#0A192F]"
          >
            {t("nav.enquire")}
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6 text-slate-700" /> : <Menu className="h-6 w-6 text-slate-700" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white lg:hidden">
          <div className="space-y-1 px-4 py-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2 text-sm font-medium ${
                    isActive ? "bg-slate-100 text-[#0F2942]" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center justify-between pt-2">
              <LanguageSwitcher />
              <Button
                onClick={() => { setOpen(false); setShowEnq(true); }}
                className="bg-[#0F2942] text-white hover:bg-[#0A192F]"
              >
                {t("nav.enquire")}
              </Button>
            </div>
          </div>
        </div>
      )}
      <EnquiryDialog open={showEnq} onOpenChange={setShowEnq} />
    </header>
  );
}
